<template>
  <div class="RuleEditor">
    <div
      :id="idFromSource"
      class="accordion-collapse collapse mt-3 px-3"
      aria-labelledby="flush-headingOne"
      data-bs-parent="#accordionFlushExample"
    >
      <div v-if="error" class="alert alert-danger" role="alert">
        Error: {{ reason }}
      </div>
      <div class="row">
        <!-- Source Fields -->
        <div class="col-md-3">
          Source(s)<br />
          <table
            class="table table-sm col-md-3"
            style="width: 100%"
            id="sourceTable"
          >
            <colgroup>
              <col span="1" style="width: 10%;" />
              <col span="1" style="width: 45%;" />
              <col span="1" style="width: 45%;" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Field</th>
                <th scope="col">Test Value</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(sourcefield,
                sourcefieldIndex) in renderSelectedSourceFields"
                :key="sourcefieldIndex"
              >
                <td
                  :id="
                    'count' +
                      source +
                      sourcefield.replace(/[^a-zA-Z0-9]/g, '') +
                      rule.rule_id
                  "
                >
                  {{ alphaCounter[sourcefieldIndex] }}
                </td>
                <td
                  style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right:5%"
                >
                  {{ sourcefield }}
                </td>
                <td>
                  <input
                    v-if="fieldValueMapped && method !== 'map'"
                    type="text"
                    class="form-control form-control-sm"
                    style="width: -webkit-fill-available;"
                    v-model="fieldValues[sourcefield][1]"
                    v-on:input="debounceFieldValue()"
                  />
                  <div v-else style="display: flex;">
                    <div class="col-10" v-if="fieldValueMapped">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        style="width: -webkit-fill-available;"
                        v-model="fieldValues[sourcefield][1]"
                        v-on:input="debounceFieldValue()"
                      />
                    </div>
                    <div class="col-2 px-1">
                      <input
                        type="checkbox"
                        class="form-check-input mt-2"
                        :value="sourcefield"
                        v-model="stringMatch"
                        v-on:input="debounceFieldValue()"
                        data-toggle="tooltip"
                        data-placement="right"
                        title="Enable string comparison"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            class="btn btn-secondary btn-sm mb-3"
            @click="
              $emit('openChangeSourceFieldsModal', renderSelectedSourceFields)
            "
          >
            Add Source Field
          </button>
        </div>

        <!-- Rule-Specific Information: Method (passthru), Function (function), Table Map (map), Function (qualify-function), Lookup Table (qualify-lookup) -->
        <div
          v-bind:class="{
            'col-5': method === 'map',
            'col-3': method !== 'map',
          }"
        >
          <div v-if="method === 'passthru'">
            Method
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                id="map"
                value="insurer_code_map"
                v-model="methodType"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="map">
                Insurer Code Map
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                id="passthru"
                value="direct"
                v-model="methodType"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="passthru">
                Do not Change (Pass Direct)
              </label>
            </div>
          </div>
          <div v-else-if="method === 'function'">
            <label class="form-check-label" for="function">
              Function
            </label>
            <select
              class="form-select form-select-sm mb-2"
              v-model="functionType"
              v-on:input="debounceMethod()"
            >
              <option value="sum">SUM</option>
              <option value="avg">AVG</option>
              <option value="max">MAX</option>
              <option value="min">MIN</option>
              <option value="custom">CUSTOM</option>
            </select>
            <div class="mb-2" v-if="functionType === 'custom'">
              <label for="customFunction" class="form-label">
                Custom Function
              </label>
              <div class="input-group input-group-sm">
                <input
                  v-model="functionExp"
                  class="form-control"
                  v-on:input="debounceMethod()"
                />
                <button
                  class="btn btn-outline-secondary col-md-2"
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#expandedFuncModal"
                  @click="
                    expandedFunctionExp = functionExp;
                    $emit('updateExpandedFunctionExp', expandedFunctionExp);
                  "
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-arrow-up-right"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M14 2.5a.5.5 0 0 0-.5-.5h-6a.5.5 0 0 0 0 1h4.793L2.146 13.146a.5.5 0 0 0 .708.708L13 3.707V8.5a.5.5 0 0 0 1 0v-6z"
                    />
                  </svg>
                </button>
              </div>
              <a
                href="#"
                data-bs-toggle="modal"
                data-bs-target="#functionsHelp"
                style="font-size: x-small;"
              >
                Supported Functions Help
              </a>
            </div>
            <div
              v-else-if="['sum', 'avg', 'max', 'min'].includes(functionType)"
              class="row mb-2"
            >
              <div class="col-md-8">
                <label for="rounding" class="form-label">Rounding</label>
                <select
                  id="rounding"
                  class="form-select form-select-sm"
                  v-model="roundType"
                  v-on:input="debounceMethod()"
                >
                  <option value="roundup">Round Up</option>
                  <option value="rounddown">Round Down</option>
                  <option value="donotround">Do not round</option>
                </select>
              </div>
              <div class="col-md-4">
                <label for="decimals" class="form-label">Decimals</label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="roundDecimals"
                  v-on:input="debounceMethod()"
                />
              </div>
            </div>
          </div>
          <div
            v-if="method === 'map' && renderSelectedSourceFields.length <= 3"
          >
            <label class="form-check-label" for="tableMap">
              Table Map
            </label>
            <table class="table" id="mapTable">
              <thead>
                <tr style="display: flex; flex-wrap: wrap;">
                  <th
                    v-for="(field, index) in renderSelectedSourceFields"
                    class="col-sm-3"
                    :key="index"
                    style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right:1%"
                  >
                    {{ field }}
                  </th>
                  <th class="col-sm-3">Output</th>
                </tr>
              </thead>
              <tbody style="display: flex; flex-wrap: wrap;">
                <div
                  :id="'mapRow' + source + i + rule.rule_id"
                  v-for="i in rows"
                  :key="i"
                >
                  <tr
                    v-if="tableMap[i - 1]"
                    style="display: flex; flex-wrap: wrap; border-bottom: hidden;"
                  >
                    <td
                      class="col-sm-3"
                      v-for="(field, index) in renderSelectedSourceFields"
                      :key="index"
                    >
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-on:input="debounceMethod()"
                        v-model="tableMap[i - 1][field]"
                      />
                    </td>
                    <td class="col-sm-3" style="display:flex;">
                      <input
                        type="text"
                        class="form-control form-control-sm col-sm-2"
                        v-on:input="debounceMethod()"
                        v-model="tableMap[i - 1]['output']"
                      />
                      <button
                        type="button"
                        class="btn btn-sm col-sm-1"
                        @click="deleteMapRow(i)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          class="bi bi-trash"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"
                          />
                          <path
                            fill-rule="evenodd"
                            d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                </div>
              </tbody>
            </table>

            <div class="row">
              <input
                type="number"
                min="1"
                class="form-control form-control-sm"
                style="width: 17%; margin-left: 5%;"
                v-model="numberOfRows"
              />
              <button
                type="button"
                style="width: auto;"
                class="btn btn-secondary col-sm-6"
                @click="addRow()"
              >
                Add
              </button>
            </div>
            <br />
            <a
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#tableMapSupportModal"
              style="font-size: x-small;"
              >Table Map Support</a
            >
          </div>
          <div v-else-if="method === 'qualify-function'">
            <label class="form-check-label" for="function">
              Function
            </label>
            <textarea
              class="form-control"
              rows="3"
              v-model="qualifyFunction"
              v-on:input="debounceMethod()"
            />
            <div class="form-check mt-2">
              <input
                class="form-check-input"
                type="radio"
                value="true"
                v-model="declineOn"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="flexRadioDefault1">
                Decline on True
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                value="false"
                v-model="declineOn"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="flexRadioDefault2">
                Decline on False
              </label>
            </div>
            <a
              href="#"
              data-bs-toggle="modal"
              data-bs-target="#qualifyFunSupportModal"
              style="font-size: x-small;"
              >Qualify Function Support</a
            >
          </div>
          <div v-else-if="method === 'qualify-lookup'">
            <label>Lookup Table</label>
            <select
              class="form-select form-select-sm"
              v-model="newLookupTable"
              v-on:input="debounceMethod()"
            >
              <option value="Insurer Code Map">Insurer Code Map</option>
              <option
                v-for="table in tables"
                :key="table.insurer_lookup_table_id"
                :value="table.table_name"
                >{{ table.table_name }}
              </option>
            </select>
            <div class="form-check mt-3">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="acceptOnMissing"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="flexCheckDefault">
                Accept on missing
              </label>
            </div>
          </div>
        </div>

        <!-- Calculated Value -->
        <div
          v-bind:class="{
            'col-2': method === 'map',
            'col-3': method !== 'map',
          }"
        >
          Calculated Value
          <div style="display:flex;">
            <div class="col-8 px-1">
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="calculatedValue"
                disabled
              />
            </div>
            <div class="col-4">
              <div
                v-if="
                  method === 'passthru' ||
                    method === 'function' ||
                    method === 'map' ||
                    method === 'qualify-lookup'
                "
              >
                <div
                  v-if="executed === 'loading'"
                  class="spinner-border text-danger spinner-border-sm"
                />
                <span v-else-if="executed" class="badge bg-success"
                  >Success
                </span>
                <span v-else class="badge bg-danger">Failed</span>
              </div>
              <div v-else-if="method === 'qualify-function'">
                <div
                  v-if="executed === 'loading'"
                  class="spinner-border text-danger spinner-border-sm"
                />
                <span
                  v-else-if="declined === true || declined == null"
                  class="badge bg-info"
                  >Decline
                </span>
                <!-- **can change declined === null -->
                <span v-else-if="declined === false" class="badge bg-secondary"
                  >Accept
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="
              method === 'passthru' || method === 'function' || method === 'map'
            "
          >
            <div class="form-check mt-3">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="contOnFail"
                v-on:input="debounceMethod()"
              />
              <label class="form-check-label" for="flexCheckDefault">
                Continue on fail
              </label>
            </div>
            <div v-if="contOnFail" class="col-8">
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="failValue"
                v-on:input="debounceMethod()"
              />
            </div>
          </div>
        </div>

        <!-- Output (passthru, function, map) -->
        <div
          v-bind:class="{
            'col-2': method === 'map',
            'col-3': method !== 'map',
          }"
        >
          <div
            v-if="
              method === 'passthru' || method === 'function' || method === 'map'
            "
          >
            Output
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                id="calculator"
                value="calculator"
                v-model="output"
              />
              <label class="form-check-label" for="calculator">
                Calculator Field
              </label>
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                id="custom"
                value="custom"
                v-model="output"
              />
              <label class="form-check-label" for="custom">
                Custom Field Value
              </label>
            </div>
            <div
              v-if="output === 'calculator'"
            >
            <!-- <input
            class="form-select" list="calc_fields"
            
            
            />
            <datalist id="calc_fields" style="font-size: x-small;"> -->
            <autocomplete  :on-submit="handleACSubmit" :search="search" :default-value=this.newSelectedCalculatorField ></autocomplete>

              <!-- <option
                style="font-size: x-small;"
                v-for="field in calculatorFields"  
                v-bind:value="field.parameter_name"
                :key="field.parameter_name"
                >
                {{ field.parameter_name }}
              </option> -->
            <!-- </datalist> -->
          <!-- </select> -->
          </div>
            <input
              type="text"
              class="form-control"
              v-model="newSelectedCustomField"
              v-else-if="output === 'custom'"
            />
          </div>
          <div
            v-else-if="
              method === 'qualify-function' || method === 'qualify-lookup'
            "
          >
            Qualifying Action on Failure
            <label for="declinemsg" class="mt-1">Decline Message</label>
            <input
              type="text"
              class="form-control"
              v-model="declineMsg"
              placeholder="eg. Declined"
            />
            <div class="form-check mt-2">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="referOnFail"
              />
              <label class="form-check-label" for="flexCheckDefault">
                Refer on Failure
              </label>
            </div>
            <label for="referralmsg">Referral Message</label>
            <input
              type="text"
              class="form-control"
              v-model="referMsg"
              placeholder="eg. Referred"
            />
          </div>
        </div>
      </div>

      <!-- Table Map (map for when there are many source fields: spills into another row) -->
      <div v-if="method === 'map' && renderSelectedSourceFields.length > 3">
        <label class="form-check-label" for="tableMap">
          Table Map
        </label>
        <table class="table" id="mapTable">
          <thead>
            <tr style="display: flex; flex-wrap: wrap;">
              <th
                class="col-sm-1"
                v-for="(field, index) in renderSelectedSourceFields"
                :key="index"
                style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right:1%"
              >
                {{ field }}
              </th>
              <th class="col-sm-1">Output</th>
            </tr>
          </thead>
          <tbody style="display: flex; flex-wrap: wrap;">
            <div
              :id="'mapRow' + source + i + rule.rule_id"
              v-for="i in rows"
              :key="i"
            >
              <tr
                v-if="tableMap[i - 1]"
                style="display: flex; flex-wrap: wrap; border-bottom:hidden;"
              >
                <td
                  class="col-sm-1"
                  v-for="(field, index) in renderSelectedSourceFields"
                  :key="index"
                >
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-on:input="debounceMethod()"
                    v-model="tableMap[i - 1][field]"
                  />
                </td>
                <td class="col-sm-1" style="display:flex;">
                  <input
                    type="text"
                    class="form-control form-control-sm col-sm-2"
                    v-on:input="debounceMethod()"
                    v-model="tableMap[i - 1]['output']"
                  />
                  <button
                    type="button"
                    class="btn btn-sm col-sm-1"
                    @click="deleteMapRow(i)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-trash"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"
                      />
                      <path
                        fill-rule="evenodd"
                        d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            </div>
          </tbody>
        </table>

        <div class="row">
          <input
            type="number"
            min="1"
            class="form-control form-control-sm"
            style="width: 7%; margin-left: 2%;"
            v-model="numberOfRows"
          />
          <button
            type="button"
            style="width: auto;"
            class="btn btn-secondary col-sm-6"
            @click="addRow()"
          >
            Add
          </button>
        </div>
        <br />
        <a
          href="#"
          data-bs-toggle="modal"
          data-bs-target="#tableMapSupportModal"
          style="font-size: x-small;"
          >Table Map Support</a
        >
      </div>

      <!-- Cancel and Save Changes Buttons -->
      <div class="form-group mt-3 mb-3" style="text-align:right;">
        <button
          class="btn btn-secondary"
          type="button"
          @click="cancelChanges()"
        >
          Cancel
        </button>
        <div class="divider" />
        <button class="btn btn-primary" type="button" @click="saveChanges()">
          Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import config from "../../../config";
import _ from "lodash";
import $ from "jquery";
import '@trevoreyre/autocomplete-vue/dist/style.css'

export default {
  name: "RuleEditor",
  props: {
    ruleTemp: Object,
    functionExpTemp: String,
    method: String,
    source: String, // "Above", "Edit", "Below", "End"
    calculator_id: Number,
    insurer_id: Number,
    calculatorFields: Array,
    tables: Array,
    renderSelectedSourceFieldsTemp: Array,
  },
  emits: [
    "clearRenderSelectedSourceFields",
    "openAddSourceModal",
    "cacheData",
    "updateRules",
    "successToast",
    "updateExpandedFunctionExp",
  ],
  data() {
    return {
      reason: "",
      methodType: "",
      functionType: "",
      functionExp: "",
      roundType: "",
      qualifyFunction: "",
      declineOn: "",
      acceptOnMissing: "",
      stringMatch: [],
      calculatedValue: "",
      executed: false,
      declined: "",
      accept: "",
      contOnFail: false,
      failValue: "",
      output: null,
      newSelectedCustomField: null,
      declineMsg: "",
      referOnFail: false,
      referMsg: "",
      rows: 0,
      numberOfRows: 1,
      error: false,
      tableMap: [],
      newLookupTable: [],
      alphaCounter: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
        "AA",
        "AB",
        "AC",
        "AD",
        "AE",
        "AF",
        "AG",
        "AH",
        "AI",
        "AJ",
        "AK",
        "AL",
        "AM",
        "AN",
        "AO",
        "AP",
        "AQ",
        "AR",
        "AS",
        "AT",
        "AU",
        "AV",
        "AW",
        "AX",
        "AY",
        "AZ",
      ],
      renderSelectedSourceFields: this.renderSelectedSourceFieldsTemp,
      rule: this.ruleTemp,
      expandedFunctionExp: "",
      newSelectedCalculatorField: null,
      roundDecimals: 0,
      fieldValueMapped: false,
      sourcefields: [],
      fieldValues: {},
      sourceLoader: null,
    };
  },
  watch: {
    ruleTemp() {
      this.rule = Object.assign({}, this.ruleTemp);
    },
    renderSelectedSourceFieldsTemp() {
      this.renderSelectedSourceFields = this.renderSelectedSourceFieldsTemp;
    },
    functionExpTemp() {
      this.functionExp = this.functionExpTemp;
      this.debounceMethod();
    },
    functionExp: _.debounce(function() {
      if (!Object.values(this.fieldValues).every((x) => x === "")) {
        this.ruleCheck();
      }
    }, 500),
    renderSelectedSourceFields() {
      // Got rid of debounce since we need to do mapFieldValues asap or else there'll be errors
      this.fieldValueMapped = false;
      this.mapFieldValues();
      this.fieldValueMapped = true;
      if (!Object.values(this.fieldValues).every((x) => x === "")) {
        this.ruleCheck();
      }
    },
    tableMap: _.debounce(function() {
      if (!Object.values(this.fieldValues).every((x) => x === "")) {
        this.ruleCheck();
      }
    }, 500),
  },
  computed: {
    idFromSource() {
      if (this.source == "Above" || this.source == "Below") {
        return "addRule" + this.source + this.rule.sort;
      } else if (this.source == "End") {
        return "addRule" + this.source;
      } else {
        return "editRule" + this.rule.rule_id;
      }
    },
  },
  methods: {
    initializeValues: function(
      newTableMap,
      newFieldValues,
      newStringMatch,
      newSourceFields
    ) {
      this.tableMap = newTableMap;
      this.rows = Object.keys(this.tableMap).length;
      this.fieldValueMapped = false;
      this.fieldValues = newFieldValues;
      this.stringMatch = newStringMatch;
      this.ruleCheck(); // **check order of when ruleCheck() should be called
      this.renderSelectedSourceFields = newSourceFields;

      // Add all source fields to fieldValues, even if it doesn't have a filled corresponding fieldValue by default
      // This is the replacement for the JQuery used previously
      for (let i = 0; i < this.renderSelectedSourceFields.length; i++) {
        var sourcefield = this.renderSelectedSourceFields[i];
        this.fieldValues[`${sourcefield}`] = [
          this.alphaCounter[i],
          sourcefield in this.fieldValues
            ? this.fieldValues[sourcefield][1]
            : "",
        ];
      }
      this.fieldValueMapped = true;

      // For inputs v-model in Edit mode
      this.qualifyFunction = this.rule.rule_data.function;
      this.methodType = this.rule.rule_data.type;
      this.functionType = this.rule.rule_data.function_type;
      this.functionExp = this.rule.rule_data.function;
      this.roundType = this.rule.rule_data.round_type;
      this.roundDecimals = this.rule.rule_data.round_decimals;
      this.declineOn = this.rule.rule_data.decline_on;
      this.newLookupTable = this.rule.rule_data.lookup_table;
      this.acceptOnMissing = this.rule.rule_data.accept_on_missing;
      this.contOnFail = this.rule.rule_data.cont_on_fail;
      this.failValue = this.rule.rule_data.default_value;
      this.output = this.rule.output_type;

      this.newSelectedCalculatorField = this.rule.calculator_param;
      this.newSelectedCustomField = this.rule.custom_field;
      this.declineMsg = this.rule.decline_message;
      this.referOnFail = this.rule.refer_on_fail;
      this.referMsg = this.rule.refer_message;
    },
    mapFieldValues() {
      for (let i = 0; i < this.renderSelectedSourceFields.length; i++) {
        var sourcefield = this.renderSelectedSourceFields[i];
        this.fieldValues[`${sourcefield}`] = [
          this.alphaCounter[i],
          sourcefield in this.fieldValues
            ? this.fieldValues[sourcefield][1]
            : "",
        ];
      }
    },
    deleteMapRow(i) {
      if (this.tableMap[i - 1]) delete this.tableMap[i - 1];
      this.debounceMethod();
    },
    addRow() {
      for (let i = 0; i < this.numberOfRows; i++) {
        this.tableMap[this.rows] = {};
        this.rows++;
      }
    },
    cancelChanges() {
      // Cancels adding or updating a rule
      // Hide the add/edit bar
      $("#" + this.idFromSource).collapse("hide");
      // Clearing prevents the loader in Calculated Value from going on forever
      this.clear();
    },
    async saveChanges() {
      var ruleData = null;
      var defaultValue = null;
      if (this.contOnFail) {
        defaultValue = this.failValue;
      }

      if (this.method === "passthru") {
        if (this.source != "Edit" && !this.methodType) {
          this.error = true;
          this.reason = "Method cannot be empty!";
        } else {
          this.error = false;
          ruleData = {
            type: this.methodType,
            test_values: this.fieldValues,
            cont_on_fail: this.contOnFail,
            default_value: defaultValue,
          };
        }
      } else if (this.method === "function") {
        if (this.source != "Edit" && !this.functionType) {
          this.error = true;
          this.reason = "Function cannot be empty!";
        } else {
          this.error = false;
          ruleData = {
            function_type: this.functionType,
            function: this.functionExp,
            round_type: this.roundType,
            round_decimals: this.roundDecimals,
            test_values: this.fieldValues,
            cont_on_fail: this.contOnFail,
            default_value: defaultValue,
          };
        }
      } else if (this.method === "map") {
        this.stringMatch = this.stringMatch.filter((n) =>
          Object.keys(this.fieldValues).includes(n)
        );
        var tableMapValues = [];
        if (this.source != "Edit") {
          tableMapValues = Object.values(this.tableMap).filter(
            (value) => Object.keys(value).length !== 0
          );
        } else {
          tableMapValues = Object.values(this.tableMap);
        }
        if (this.source != "Edit" && _.isEmpty(tableMapValues)) {
          this.error = true;
          this.reason = "Table Map cannot be empty!";
        } else {
          this.error = false;
          ruleData = {
            table_map: tableMapValues,
            test_values: this.fieldValues,
            string_match: this.stringMatch,
            cont_on_fail: this.contOnFail,
            default_value: defaultValue,
          };
        }
      } else if (this.method === "qualify-function") {
        if (this.source != "Edit" && !this.qualifyFunction) {
          this.error = true;
          this.reason = "Function cannot be empty!";
        } else if (this.source != "Edit" && !this.declineOn) {
          this.error = true;
          this.reason = "Decline condition cannot be empty!";
        } else {
          this.error = false;
          ruleData = {
            function: this.qualifyFunction,
            decline_on:
              this.declineOn === "true"
                ? true
                : this.declineOn === "false"
                ? false
                : null,
            test_values: this.fieldValues,
          };
        }
      } else if (this.method === "qualify-lookup") {
        if (this.source != "Edit" && !this.newLookupTable) {
          this.error = true;
          this.reason = "Lookup table cannot be empty!";
        } else {
          this.error = false;
          ruleData = {
            lookup_table: this.newLookupTable,
            test_values: this.fieldValues,
            accept_on_missing: this.acceptOnMissing,
          };
        }
      }

      var requestOptions = {};
      if (!this.error) {
        var sources = {};
        this.renderSelectedSourceFields.forEach((item, index) => {
          sources[this.alphaCounter[index]] = item;
        });
        if (this.source != "Edit") {
          requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              data: {
                sort: this.rule.sort,
                ruleset_id: this.$route.params.id,
                rule_type: this.method,
                rule_data: ruleData,
                source_fields: sources,
                output_type: this.output,
                calculator_param: this.newSelectedCalculatorField,
                custom_field: this.newSelectedCustomField,
                decline_message: this.declineMsg ? this.declineMsg : "Declined",
                refer_on_fail: this.referOnFail,
                refer_message: this.referMsg ? this.referMsg : "Referred",
              },
            }),
          };
        } else {
          requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              data: {
                rule_id: this.rule.rule_id,
                rule_type: this.method,
                rule_data: ruleData,
                source_fields: sources,
                output_type: this.output,
                calculator_param: this.newSelectedCalculatorField,
                custom_field: this.newSelectedCustomField,
                decline_message: this.declineMsg ? this.declineMsg : "Declined",
                refer_on_fail: this.referOnFail,
                refer_message: this.referMsg ? this.referMsg : "Referred",
              },
            }),
          };
        }
        // The ternary determines the URL
        const response = await fetch(
          this.source == "Edit"
            ? config.baseURL + "/update_rule"
            : config.baseURL + "/add_rule_" + this.source.toLowerCase(),
          requestOptions
        );
        const data = await response.json();
        // console.log(data);

        if (data["Added"]) {
          $("#" + this.idFromSource).collapse("hide");
          this.rules = data["rules"];
          this.rules.forEach((element) => {
            element.source_fields = Object.values(element.source_fields);
          });
          sessionStorage.setItem("rules", JSON.stringify(data["rules"]));
          this.$emit("updateRules", this.rules);

          if (this.source == "Edit") {
            this.$emit("successToast", "Rule updated.");
            $("#accordion-header" + this.rule.rule_id).css({
              visibility: "visible",
              height: "auto",
            });
          } else {
            this.$emit("successToast", "Rule created.");
          }

          this.$emit("cacheData");
          this.clear();
        } else {
          this.error = true;
          this.reason = data["reason"];
        }
      }
    },
    async getSourceFields() {
      this.sourceLoader = true;
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          data: {
            ruleset_id: this.$route.params.id,
          },
        }),
      };
      const response = await fetch(
        config.baseURL + "/get_all_req_calculated_fields",
        requestOptions
      );
      const data = await response.json();
      let sourceFields = JSON.parse(
        sessionStorage.getItem("req_quotey_fields")
      );
      sourceFields.push(...data);
      this.sourcefields = sourceFields;
      this.sourceLoader = false;
    },
    debounceMethod: _.debounce(function() {
      if (!Object.values(this.fieldValues).every((x) => x === "")) {
        this.ruleCheck();
      }
    }, 500),
    debounceFieldValue() {
      const l = new Set(this.renderSelectedSourceFields);
      for (let prop of Object.keys(this.fieldValues)) {
        if (!l.has(prop)) {
          delete this.fieldValues[prop];
        }
      }
      this.debounceMethod();
    },
    async ruleCheck() {
      this.executed = "loading";
      var ruleData = null;
      var defaultValue = null;

      if (this.contOnFail) {
        defaultValue = this.failValue;
      }

      if (this.method === "passthru") {
        ruleData = {
          type: this.methodType,
          cont_on_fail: this.contOnFail,
          default_value: defaultValue,
        };
      } else if (this.method === "function") {
        ruleData = {
          function_type: this.functionType,
          function: this.functionExp,
          round_type: this.roundType,
          round_decimals: this.roundDecimals,
          cont_on_fail: this.contOnFail,
          default_value: defaultValue,
        };
      } else if (this.method === "map") {
        this.stringMatch = this.stringMatch.filter((n) =>
          Object.keys(this.fieldValues).includes(n)
        );
        ruleData = {
          table_map: Object.values(this.tableMap),
          string_match: this.stringMatch,
          cont_on_fail: this.contOnFail,
          default_value: defaultValue,
        };
      } else if (this.method === "qualify-function") {
        ruleData = {
          function: this.qualifyFunction,
          decline_on:
            this.declineOn === "true"
              ? true
              : this.declineOn === "false"
              ? false
              : null,
        };
      } else if (this.method === "qualify-lookup") {
        ruleData = {
          lookup_table: this.newLookupTable,
          accept_on_missing: this.acceptOnMissing,
        };
      }
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          data: {
            ruleset_id: this.$route.params.id,
            calculator_id: this.calculator_id,
            insurer_id: this.insurer_id,
            rule_type: this.method,
            rule_data: ruleData,
            source_fields: this.fieldValues,
          },
        }),
      };
      const response = await fetch(
        config.baseURL + "/execute_rule_check",
        requestOptions
      );
      const data = await response.json();
      // console.log(data);
      this.calculatedValue = data["Value"];
      this.executed = data["Executed"];
      this.declined = data["decline"];
      this.accept = data["accept"];
    },
    clear() {
      // **Remove stuff we dont need (clear is only used in addRule)
      this.error = false;
      this.reason = "";
      this.qualifyFunction = "";
      this.newLookupTable = "";
      this.declineOn = "";
      this.declineMsg = "";
      this.referOnFail = false;
      this.referMsg = "";
      this.declined = "";
      this.accept = false;
      this.methodType = "";
      if (this.source != "Edit") {
        this.$emit("clearRenderSelectedSourceFields");
        this.$emit("updateExpandedFunctionExp", "");
      }
      this.output = null;
      this.newSelectedCalculatorField = null;
      this.newSelectedCustomField = null;
      this.calculatedValue = "";
      this.executed = false;
      this.functionType = "";
      this.functionExp = "";
      this.roundType = "donotround";
      this.roundDecimals = 0;
      this.numberOfRows = 1;
      this.contOnFail = false;
      this.failValue = "";
      this.stringMatch = [];
      this.acceptOnMissing = false;
      this.tableMap = {};
    },
    search(input) {
      let parms = this.calculatorFields.map(x => x.parameter_name)
      if (input.length < 1) { return parms}
      return parms.filter(parm => {
        return parm.toLowerCase()
          .includes(input.toLowerCase())
      })
    },
    handleACSubmit(result) {
      this.newSelectedCalculatorField = result;
    }
  },
  mounted() {
    this.rule = this.ruleTemp;
    //console.log("mounted");
  },
};
</script>

<style scoped>
#sourceTable {
  table-layout: fixed;
  width: 100%;
}

.divider {
  width: 10px;
  height: auto;
  display: inline-block;
}
</style>
